import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const resolveApiBaseUrl = () => {
  if (typeof process !== 'undefined' && process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location || {};

    if (!protocol || !hostname) {
      return 'http://localhost:3000/api';
    }

    if (protocol === 'http:' || protocol === 'https:') {
      const port = window.location.port
        ? `:${window.location.port}`
        : protocol === 'https:'
          ? ''
          : window.location.port || '';

      return `${protocol}//${hostname}${port}/api`;
    }
  }

  return 'http://localhost:3000/api';
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create API instance with useMemo to prevent unnecessary recreations
  const api = useMemo(() => {
    const baseURL = resolveApiBaseUrl();

    const instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    instance.interceptors.request.use((config) => {
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers) {
          if (typeof config.headers.delete === 'function') {
            config.headers.delete('Content-Type');
            config.headers.delete('content-type');
          } else {
            delete config.headers['Content-Type'];
            delete config.headers['content-type'];
          }
        }
      }
      return config;
    });

    // Add response interceptor to handle 401s and 403 GYM_PLAN_INACTIVE
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          setToken(null);
          setCurrentUser(null);
        }
        if (
          error.response?.status === 403
          && ['GYM_PLAN_INACTIVE', 'GYM_BILLING_EXPIRED'].includes(error.response?.data?.error)
        ) {
          const message = error.response.data.message || 'Utente non attivo. Contatta la palestra per maggiori informazioni.';
          try {
            sessionStorage.setItem('login_message', message);
            sessionStorage.setItem('login_message_code', error.response.data.error);
          } catch (e) {}
          localStorage.removeItem('auth_token');
          setToken(null);
          setCurrentUser(null);
        }
        return Promise.reject(error);
      }
    );
  
    return instance;
  }, [token]);

  // Verify token on mount or token change
  useEffect(() => {
    let mounted = true;

    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.post('/auth.php?action=verify');
        if (mounted && response.data?.user) {
          setCurrentUser(response.data.user);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        if (mounted) {
          logout();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    verifyToken();
    return () => {
      mounted = false;
    };
  }, [token, api]);

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await api.post('/auth.php?action=login', {
        username,
        password
      });
      
      const { token: newToken, user } = response.data;
      
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      setCurrentUser(user);
      
      return user;
    } catch (err) {
      // 🔒 Gestione specifica per accesso negato alla webapp
      if (err.response?.status === 403 && err.response?.data?.error === 'ACCESS_DENIED_WEBAPP') {
        const errorMessage = err.response.data.message;
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // 🔒 Gestione specifica per account sospeso
      if (err.response?.status === 403 && err.response?.data?.error === 'ACCOUNT_SUSPENDED') {
        const errorMessage = err.response.data.message || 'Account sospeso dalla palestra';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      // 🔒 Abbonamento palestra scaduto (trainer/membro) o piano disattivato
      if (err.response?.status === 403 && err.response?.data?.error === 'GYM_BILLING_EXPIRED') {
        const errorMessage = err.response.data.message
          || 'L\'abbonamento della palestra è scaduto. Contatta il gestore o assistenza@fitgymtrack.com.';
        setError(errorMessage);
        const e = new Error(errorMessage);
        e.loginCode = 'GYM_BILLING_EXPIRED';
        throw e;
      }
      if (err.response?.status === 403 && err.response?.data?.error === 'GYM_PLAN_INACTIVE') {
        const errorMessage = err.response.data.message || 'Utente non attivo. Contatta la palestra per maggiori informazioni.';
        setError(errorMessage);
        const e = new Error(errorMessage);
        e.loginCode = 'GYM_PLAN_INACTIVE';
        throw e;
      }
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Errore durante il login';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };
  
  const logout = async () => {
    try {
      if (token) {
        await api.post('/auth.php?action=logout', { token });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('auth_token');
      setToken(null);
      setCurrentUser(null);
    }
  };

  const getAuthenticatedApi = () => api;

  const refreshCurrentUser = useCallback(async () => {
    if (!token) {
      return null;
    }
    try {
      const response = await api.post('/auth.php?action=verify');
      if (response.data?.user) {
        setCurrentUser(response.data.user);
        return response.data.user;
      }
    } catch (err) {
      console.error('refreshCurrentUser:', err);
    }
    return null;
  }, [api, token]);

  const isAdmin = () => currentUser?.role_name === 'admin';
  
  // Funzione per verificare se l'utente è standalone
  const isStandalone = () => currentUser?.role_name === 'standalone';
  
  // Funzione per verificare se l'utente è gym
  const isGym = () => currentUser?.role_name === 'gym';
  
  // Funzione per verificare se l'utente è trainer
  const isTrainer = () => currentUser?.role_name === 'trainer';
  
  const hasRole = (roleName) => currentUser?.role_name === roleName;

  // WEBAPP-031: Piano palestra (start | premium | elite); utile per nascondere calendario/corsi
  const gymPlan = () => currentUser?.gym_plan || 'premium';
  const isGymPlanStart = () => gymPlan() === 'start';
  const isGymPlanActive = () => currentUser?.gym_plan_active !== 0;

  const value = {
    currentUser,
    user: currentUser, // Alias per compatibilità
    loading,
    error,
    login,
    logout,
    isAdmin,
    isStandalone,
    isGym,
    isTrainer,
    hasRole,
    getAuthenticatedApi,
    refreshCurrentUser,
    gymPlan,
    isGymPlanStart,
    isGymPlanActive
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};